import React, { useState } from 'react';
import Link from 'next/link';
import PropTypes from 'prop-types';
import { Container } from 'components/base';
import useModalContext from 'components/context/modal';
import copyToClipboard from 'utils/copyToClipboard';
import { formatPrice } from 'utils/format';
import referralApi from 'ecommerce/api/referral';
import usersApi from 'ecommerce/api/users';
import useUserContext from 'ecommerce/context/user';
import useApi from 'ecommerce/lib/useApi';

import { Responsive, ctaTracker } from '../landing/shared';
import { Head, Layout } from '../layout';
import PdpIcon from '../product/pdpIcon';
import { Dots, FAQ_COMMON, Faq, HeroFacts, HowItWorks, TERMS_HREF } from './shared';

// Signed-in /refer-friend (docs/refer-friend.md). Pencil frames "Refer a Friend — Signed In",
// "— Mobile (Signed In)" and "— Signed In States". Usage and payouts come from
// GET api/v1/referral/stats, the balance from /users/me.
const track = ctaTracker('refer_friend', { signed_in: true });

const SHARE_URL = 'https://www.parabo.press/';
const CREDIT_HISTORY_HREF = '/user/credit';
const SHOP_HREF = '/shop';
// The full list lives in the credit history.
const ACTIVITY_ROWS = 3;

const FAQ = [
  ...FAQ_COMMON,
  {
    caption: 'Where do friends enter my code?',
    description: 'At checkout on their first order, in the Parabo Press app or on the web.',
  },
];

const shareMessage = (code) =>
  `I order my photo prints from Parabo Press. Use my code ${code} for $10 off your first order at parabo.press`;

// Whole dollars without cents ($30), anything else with them.
const formatAmount = ({ amount, currency }) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
  }).format(amount);

const formatDate = (value) =>
  new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const referralsTitle = ({ used, limit }) => {
  if (used === 0) return 'No friends have used your code yet';
  if (used >= limit) return `Your code has been used ${limit} times`;
  return used === 1 ? '1 friend has used your code' : `${used} friends have used your code`;
};

const ArrowLink = ({ href, children, onClick, size = 16 }) => (
  <Link href={href} className="rf-arrow-link" onClick={onClick}>
    {children}
    <PdpIcon name="arrowRight" size={size} />
  </Link>
);

ArrowLink.propTypes = {
  href: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  size: PropTypes.number,
};

const ShareCard = ({ code, enabled }) => {
  const modalContext = useModalContext();
  const message = shareMessage(code);

  const copy = (value, toast, cta) => {
    copyToClipboard(value);
    modalContext.toast(toast, { type: 'success' });
    track(cta, 'share_card')();
  };

  // Phones get the system share sheet. Without the Web Share API the message is copied instead.
  const handleShare = async () => {
    track('share', 'share_card')();
    if (!navigator.share) {
      copy(message, 'Message copied', 'copy_message');
      return;
    }
    try {
      await navigator.share({ text: message });
    } catch (error) {
      // AbortError when the share sheet is dismissed.
    }
  };

  const sendLinks = [
    {
      key: 'email',
      label: 'Email',
      icon: 'mail',
      href: `mailto:?subject=${encodeURIComponent('$10 off your first Parabo Press order')}&body=${encodeURIComponent(message)}`,
    },
    {
      key: 'facebook',
      label: 'Facebook',
      icon: 'facebook',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(SHARE_URL)}`,
    },
    {
      key: 'x',
      label: 'Post on X',
      href: `https://x.com/intent/post?text=${encodeURIComponent(message)}`,
    },
  ];

  return (
    <div className="rf-share-card">
      <div className="rf-share-block">
        <p className="rf-code-label">Your code</p>
        <div className="rf-share-code-row">
          <span className="rf-share-code">{code}</span>
          {enabled && (
            <button
              type="button"
              className="rf-share-copy"
              onClick={() => copy(code, 'Code copied', 'copy_code')}
            >
              <PdpIcon name="copy" size={16} />
              <Responsive desktop="Copy code" phone="Copy" />
            </button>
          )}
        </div>
      </div>

      {/* At the cap the code no longer gives friends $10 off, so there is nothing to share. */}
      {enabled && (
        <>
          <div className="rf-share-block">
            <div className="rf-share-label-row rf-desk-flex">
              <p className="rf-code-label">Your message</p>
              <button
                type="button"
                className="rf-share-text-btn"
                onClick={() => copy(message, 'Message copied', 'copy_message')}
              >
                <PdpIcon name="copy" size={14} />
                Copy message
              </button>
            </div>
            <p className="rf-share-message">{message}</p>
          </div>

          <div className="rf-share-block rf-desk-flex">
            <p className="rf-code-label">Send it</p>
            <div className="rf-share-send">
              {sendLinks.map((link) => (
                <a
                  key={link.key}
                  href={link.href}
                  className="rf-share-send-btn"
                  target={link.key === 'email' ? undefined : '_blank'}
                  rel={link.key === 'email' ? undefined : 'noopener noreferrer'}
                  onClick={track(link.key, 'share_card')}
                >
                  {link.icon && <PdpIcon name={link.icon} size={16} />}
                  {link.label}
                </a>
              ))}
            </div>
          </div>

          <div className="rf-share-block rf-phone-flex">
            <button type="button" className="lnd-btn rf-share-btn" onClick={handleShare}>
              <PdpIcon name="share" size={17} />
              Share your code
            </button>
            <p className="rf-share-hint">
              Opens your phone’s share options, like Messages or email.
            </p>
          </div>
        </>
      )}
    </div>
  );
};

ShareCard.propTypes = {
  code: PropTypes.string,
  enabled: PropTypes.bool,
};

const Hero = ({ code, enabled }) => (
  <section className="rf-hero rf-hero--share">
    <Container className="rf-share-inner">
      <div>
        <p className="rf-eyebrow">Refer a Friend</p>
        <h1 className="rf-h1">Give $10, get $10</h1>
        <p className="rf-lead">
          <Responsive
            desktop="Share your code with friends who haven’t ordered from Parabo Press yet. They get $10 off their first order. You get $10 in Parabo credit when it ships."
            phone="Friends get $10 off their first order. You get $10 in Parabo credit when it ships, for up to 50 friends."
          />
        </p>
        <HeroFacts className="rf-hero-facts--share rf-desk-flex" />
      </div>
      <ShareCard code={code} enabled={enabled} />
    </Container>
  </section>
);

Hero.propTypes = {
  code: PropTypes.string,
  enabled: PropTypes.bool,
};

const Stat = ({ label, value }) => (
  <p className="rf-stat">
    <span className="rf-stat-label">{label}</span>
    <span className="rf-stat-value">{value}</span>
  </p>
);

Stat.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.node.isRequired,
};

const Activity = ({ credits }) =>
  credits.length > 0 ? (
    <ul className="rf-activity">
      {credits.slice(0, ACTIVITY_ROWS).map((credit) => (
        <li key={credit.id} className="rf-activity-row">
          <span className="rf-activity-left">
            <span className="rf-activity-badge rf-desk-flex">
              <PdpIcon name="gift" size={16} />
            </span>
            <span>
              <span className="rf-activity-title">Friend’s order shipped</span>
              <span className="rf-activity-date">{formatDate(credit.createdAt)}</span>
            </span>
          </span>
          <span className="rf-activity-amount">+{formatPrice(credit.amount, credit.currency)}</span>
        </li>
      ))}
    </ul>
  ) : (
    <div className="rf-activity-empty">
      <p className="rf-activity-title">Your $10 credits will show up here</p>
      <p>
        Each one arrives when a friend’s first order ships. Send your code to a few people who print
        photos, or keep it handy for the next time someone asks where you get yours.
      </p>
    </div>
  );

Activity.propTypes = {
  credits: PropTypes.arrayOf(PropTypes.object).isRequired,
};

const Balance = ({ balance }) => {
  const hasCredit = balance.amount > 0;

  return (
    <div className="rf-balance">
      <div className="rf-balance-row">
        <p className="rf-stat">
          <span className="rf-stat-label">Parabo credit balance</span>
          <span className="rf-balance-value">{formatPrice(balance.amount, balance.currency)}</span>
        </p>
        <span className="lnd-phone">
          <ArrowLink
            href={CREDIT_HISTORY_HREF}
            size={14}
            onClick={track('history', 'balance', CREDIT_HISTORY_HREF)}
          >
            History
          </ArrowLink>
        </span>
      </div>
      <p className="rf-balance-body">
        <Responsive
          desktop={`${hasCredit ? 'Your credit' : 'Credit you earn'} comes off your next order automatically at checkout. Earned credit is valid for 1 year.`}
          phone="Comes off your next order automatically at checkout. Earned credit is valid for 1 year."
        />
      </p>
      {hasCredit && (
        <Link
          href={SHOP_HREF}
          className="lnd-btn rf-balance-btn rf-desk-flex"
          onClick={track('shop', 'balance', SHOP_HREF)}
        >
          Shop prints
          <PdpIcon name="arrowRight" size={18} />
        </Link>
      )}
    </div>
  );
};

Balance.propTypes = {
  balance: PropTypes.shape({ amount: PropTypes.number, currency: PropTypes.string }).isRequired,
};

const YourReferrals = ({ stats, balance }) => {
  return (
    <section className="rf-section rf-referrals">
      <Container>
        <div className="rf-referrals-head">
          <div>
            <p className="rf-eyebrow">Your referrals</p>
            <h2 className="rf-h2 rf-h2--referrals">{referralsTitle(stats)}</h2>
          </div>
          <span className="lnd-desk">
            <ArrowLink
              href={CREDIT_HISTORY_HREF}
              onClick={track('history', 'referrals', CREDIT_HISTORY_HREF)}
            >
              View credit history
            </ArrowLink>
          </span>
        </div>

        <div className="rf-panels">
          <div className="rf-progress">
            <div className="rf-progress-head">
              <Stat label="Friends" value={`${stats.used} of ${stats.limit}`} />
              <Stat
                label={<Responsive desktop="Earned from referrals" phone="Earned" />}
                value={formatAmount(stats.earned)}
              />
              <Stat label="Uses left" value={stats.remaining} />
            </div>
            <Dots total={stats.limit} filled={stats.used} className="rf-dots--account" />
            {!stats.enabled && (
              <p className="rf-limit-note">
                <PdpIcon name="partyPopper" size={20} />
                {/* Uses count before the order ships, so this states the limit, not earnings. */}
                Your code has been used {stats.limit} times, so it no longer gives new friends $10
                off. Thanks for spreading the word.
              </p>
            )}
            <Activity credits={stats.credits} />
          </div>
          <Balance balance={balance} />
        </div>
      </Container>
    </section>
  );
};

YourReferrals.propTypes = {
  stats: PropTypes.shape({
    limit: PropTypes.number,
    used: PropTypes.number,
    remaining: PropTypes.number,
    enabled: PropTypes.bool,
    creditPerReferral: PropTypes.number,
    earned: PropTypes.shape({ amount: PropTypes.number, currency: PropTypes.string }),
    credits: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  balance: PropTypes.shape({ amount: PropTypes.number, currency: PropTypes.string }).isRequired,
};

const ReferFriendSignedIn = () => {
  const userContext = useUserContext();
  const [stats, setStats] = useState();

  useApi(async (accessToken) => {
    if (!accessToken) return;
    // credit_balance in userContext is a sessionStorage snapshot of /users/me, refetch it so the
    // balance matches the stats.
    const [{ data }, { data: details }] = await Promise.all([
      referralApi(accessToken).getStats(),
      usersApi(accessToken).getDetails(),
    ]);
    if (details) userContext.setData(details);
    if (data) setStats(data);
  }, []);

  const code = stats?.referralCode ?? userContext.data?.referral_code;
  const balance = userContext.data?.credit_balance ?? { amount: 0, currency: 'USD' };

  return (
    <>
      <Head title="Refer a Friend" noindex />

      <Layout>
        <div className="lnd rf">
          <Hero code={code} enabled={stats?.enabled ?? true} />
          {stats && <YourReferrals stats={stats} balance={balance} />}
          <HowItWorks className="rf-cream rf-section--steps" />
          <Faq items={FAQ} onTermsClick={track('terms', 'faq', TERMS_HREF)} />
        </div>
      </Layout>
    </>
  );
};

export default ReferFriendSignedIn;
